import React, { useState } from 'react';
import { showError, showWarning } from '../../utils/toast';

interface RecurringErrandFormProps {
  onSubmit: (data: RecurringErrandData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<RecurringErrandData>;
}

export interface RecurringErrandData {
  errandTypeId: string;
  title: string;
  description?: string;
  pickupLocation: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  dropoffLocation?: string;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  estimatedValue?: number;
  paymentMethod: 'wallet' | 'cash_on_delivery' | 'admin_escrow';
  recipientContact?: string;
  specialInstructions?: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay: string;
}

const RecurringErrandForm: React.FC<RecurringErrandFormProps> = ({
  onSubmit,
  onCancel,
  initialData
}) => {
  const [formData, setFormData] = useState<RecurringErrandData>({
    errandTypeId: initialData?.errandTypeId || '',
    title: initialData?.title || '',
    description: initialData?.description || '',
    pickupLocation: initialData?.pickupLocation || '',
    pickupLatitude: initialData?.pickupLatitude,
    pickupLongitude: initialData?.pickupLongitude,
    dropoffLocation: initialData?.dropoffLocation,
    dropoffLatitude: initialData?.dropoffLatitude,
    dropoffLongitude: initialData?.dropoffLongitude,
    estimatedValue: initialData?.estimatedValue,
    paymentMethod: initialData?.paymentMethod || 'cash_on_delivery',
    recipientContact: initialData?.recipientContact,
    specialInstructions: initialData?.specialInstructions,
    frequency: initialData?.frequency || 'weekly',
    dayOfWeek: initialData?.dayOfWeek,
    dayOfMonth: initialData?.dayOfMonth,
    timeOfDay: initialData?.timeOfDay || '09:00'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.errandTypeId || !formData.title || !formData.pickupLocation) {
      showWarning('Please fill in all required fields');
      return;
    }

    if (formData.frequency === 'weekly' && formData.dayOfWeek === undefined) {
      showWarning('Please select a day of the week');
      return;
    }

    if (formData.frequency === 'monthly' && formData.dayOfMonth === undefined) {
      showWarning('Please select a day of the month');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error creating recurring errand:', error);
      showError('Failed to create recurring errand');
    } finally {
      setIsSubmitting(false);
    }
  };

  const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Weekly Grocery Shopping"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pickup Location <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.pickupLocation}
          onChange={(e) => setFormData({ ...formData, pickupLocation: e.target.value })}
          placeholder="e.g., 123 Main Street, Gaborone"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Frequency <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.frequency}
            onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time <span className="text-red-500">*</span>
          </label>
          <input
            type="time"
            value={formData.timeOfDay}
            onChange={(e) => setFormData({ ...formData, timeOfDay: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          />
        </div>
      </div>

      {formData.frequency === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Day of Week <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.dayOfWeek ?? ''}
            onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          >
            <option value="">Select day</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {formData.frequency === 'monthly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Day of Month <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.dayOfMonth ?? ''}
            onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          >
            <option value="">Select day</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Recurring Errand'}
        </button>
      </div>
    </form>
  );
};

export default RecurringErrandForm;



